import { useMemo } from 'react';
import { blockedBadge, blockersOf, BLOCK_STUCK_DAYS } from '../blocked';
import { useApp } from '../store';
import { epicTint } from '../epics';
import { useSelection } from '../selection';
import type { Issue } from '../types';
import { labelColour } from '../types';
import { displayName, initials } from '../people';
import Icon from './Icon';
import VendorTag from './VendorTag';
import Priority from './Priority';
import { useSampleBoard, useSampleInert } from './SampleStrip';
import { canMoveCards, ownedByBoard } from '../ownership';
import { can } from '../permissions';
import { whatIsNew } from '../whats-new';
import { assigneeAssistant } from '../assistant';
import { marked, meansMark, meansOf } from '../issue-types';
import { agingOf, ageInDays } from '../card-age';
import { healthOfIssue } from '../progress-health';
import { effectiveProgress } from '../progress-derive';
import { ProgressMini } from './ProgressMini';
import { useAttentionChip } from './AttentionChips';
import { cardDueState, daysPast, type CardDueState } from '../phases';
import { targetPushes, dueSlip, PUSH_MARK_THRESHOLD } from '../card-slip';
import { shortDay } from '../when';
import { STATUS_LABELS, BLOCKER_HEAD } from '../types';
import { visibleComments } from '../comments';

/** The mark, in words as well as colour — see the aria-label below. */
const NEW_SAYS: Record<string, string> = {
  comment: ' — new comment',
  change: ' — changed',
  both: ' — new comment and changed',
};
const NEW_TITLE: Record<string, string> = {
  comment: 'Somebody commented since you last opened this',
  change: 'Something changed since you last opened this',
  both: 'A comment and a change since you last opened this',
};

/** The finish stamp on a Done card — "10 Sep", the same day-and-short-month the
 *  Plan screen uses for its phase windows, so dates read the same across the app. */
const finishShort = (ms: number) =>
  new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
/** The same finish, spelled out with the year for the hover title. */
const finishFull = (ms: number) =>
  new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/** The target-date chip text, by how near the date is — "Overdue 2d", "Due in
 *  2d"/"Due tomorrow"/"Due today", or a plain "Due 30 Sep" when comfortably ahead. */
function dueChip(due: string, state: CardDueState, now: number): string {
  const past = daysPast(due, now);
  if (state === 'overdue') return `Overdue ${past}d`;
  if (state === 'due-soon') return past === 0 ? 'Due today' : past === -1 ? 'Due tomorrow' : `Due in ${-past}d`;
  return `Due ${shortDay(due)}`;
}
/** The target date spelled out for the hover title — "18 September 2026". */
const dueFull = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function IssueCard({ issue }: { issue: Issue }) {
  const openIssue = useApp((s) => s.openIssue);
  // ── Bulk selection (project-bulk-card-changes) ──────────────────────────────
  // Read ONLY this card's membership and whether the board is in select MODE, so a
  // toggle re-renders just the card whose state flipped, not the whole board.
  // OUT of select mode a click OPENS the card as it always has; IN select mode a
  // click anywhere on the tile ticks it and never opens (Shift-click extends a range).
  const selected = useSelection((s) => s.ids.has(issue.id));
  const selMode = useSelection((s) => s.selectMode);
  const toggleSel = useSelection((s) => s.toggle);
  const rangeSel = useSelection((s) => s.rangeTo);
  const issueTypes = useApp((s) => s.issueTypes);
  const me = useApp((s) => s.user?.email ?? '');
  const seenAt = useApp((s) => s.cardsSeen[issue.id]);
  // What has happened here that you have not read — see src/whats-new.ts for
  // the three rules that keep this from marking everything all of the time.
  const isNew = whatIsNew(issue, me, seenAt);
  const meta = useApp((s) => s.meta);
  const areas = useApp((s) => s.areas);
  const phases = useApp((s) => s.phases);
  const labelReg = useApp((s) => s.labels);
  // The workspace working calendar (0216) — the card's progress chip counts working
  // days, so a holiday the workspace closed for doesn't read as the doer falling behind.
  const workCalendar = useApp((s) => s.workCalendar);
  // Removed comments are hidden, not erased. Counting the raw array here is how
  // a card claims three comments and shows two when one is opened.
  const shown = visibleComments(issue.comments);
  const profiles = useApp((s) => s.profiles);
  // All issues on the board, for the aging norm below (a card ages against how
  // long its peers have sat, not a fixed number — see src/card-age.ts).
  const allIssues = useApp((s) => s.issues);
  // THE EPIC'S IDENTITY COLOUR AS A MUTED FULL-CARD WASH (approved 3 Nov) — grouping
  // colour BEHIND, status/RAG/badges/progress stay their own colour in front. The hue
  // is the same one the epic chip uses (src/epics.ts: hashed by name, collision-resolved
  // against every epic on the board so a card keeps its colour for ever), carried into
  // the card ground by a color-mix in styles.css. Null when the card has no epic (it
  // stays the neutral --raise), so the board reads "these belong together, those loose".
  const epicT = useMemo(
    () =>
      issue.epic
        ? epicTint(issue.epic, [...new Set(allIssues.map((i) => i.epic).filter(Boolean))] as string[])
        : null,
    [issue.epic, allIssues],
  );
  const canMove = useApp((s) => canMoveCards(s.role, s.meta.source ?? 'tracker'));
  const sample = useSampleBoard();
  const inert = useSampleInert();
  // Whether THIS viewer could actually add a summary — the same two gates the
  // open card enforces: the edit capability (shownRole, the role being used or
  // previewed) AND that the summary is board-owned, not read-only tracker content.
  // The "+ What's this about?" nudge must not invite an edit that cannot be made.
  const summaryEditable = useApp(
    (s) => can(s.shownRole, 'editIssue') && ownedByBoard('summary', s.meta.source ?? 'tracker'),
  );
  // The block, the age and the finish — each read here so the face can carry
  // them as short markers (src/blocked.ts, src/card-age.ts). One `now` for all
  // three, so a card's markers agree with each other.
  const now = Date.now();
  // What is holding it up, as ONE line — "Blocked 3d · #142" — with the full
  // typed reason kept for the hover title, so a blocked card reads as a list row
  // again rather than a four-line paragraph towering over the column.
  const badge = blockedBadge(issue, now, allIssues);
  // How long it has sat in its working column, and whether that beats the
  // board's own norm. Null unless it is a working card with a recorded start.
  const aging = agingOf(issue, allIssues, now);
  /*
   * ONE CONTEXTUAL MARK, NOT A ROW OF THEM — the density re-cut. The foot used
   * to stack age, finish and a comment count all at once; now the face shows the
   * single most relevant one and the rest wait on the opened card. Most-telling
   * first: a finished card says WHEN it finished; a live one that has overstayed
   * the board's own norm says HOW LONG; failing both, whether it carries a
   * conversation worth opening. A card only just started shows nothing — no more
   * meaningless "0d".
   */
  const finishMark = issue.status === 'done' && issue.completedAt != null;
  // A card's own TARGET DATE takes the mark ahead of age and comments (Vinnie,
  // 13 Sep): a deadline is more telling than how long a card has sat. It shows
  // on any unfinished card that has one, styled by how near it is — overdue red,
  // due-soon amber, otherwise a muted date. A resolved card never shows it (a
  // finished card is not "due").
  const resolved = issue.status === 'done' || issue.status === 'wontdo';
  const dueMark = !resolved && issue.dueDate != null;
  const dueState = dueMark ? cardDueState(issue.dueDate, issue.status, now) : 'clear';
  const ageMark = !finishMark && !dueMark && !!aging?.aged;
  const commentMark = !finishMark && !dueMark && !ageMark && shown.length > 0;
  // How far along the work is, read against its start/due window and the pace held
  // (progress-health.ts). Present-only on in-flight cards — a resolved card carries
  // its finish mark instead, and a card with no % recorded shows nothing. A card
  // with no date of its own falls back to its phase's window, so a phased, date-less
  // card is still judged rather than left tracking.
  const cardPhase = issue.phase ? phases.find((p) => p.id === issue.phase) : undefined;
  // The card's EFFECTIVE % — a typed % wins; else it is derived from the board's
  // ticked Definition-of-Done, so a card with no number still shows how far along
  // it is (and says "· from the checklist" so the read is honest about its source).
  const eff = effectiveProgress(issue, meta?.doneChecks);
  const progHealth = healthOfIssue({ ...issue, progressPct: eff.pct }, now, cardPhase, workCalendar);
  // WHO IS DOING THE WORK leads the foot — the doer (Responsible), falling back to
  // the Accountable person when no doer is named yet, so a card is never ownerless
  // (two-part assignee). A role glyph beside the avatar says which of the two is
  // shown, by its shape (person = doer, shield = accountable), never colour alone.
  const doer = issue.responsible?.trim() ?? '';
  const footPerson = doer || (issue.assignee?.trim() ?? '');
  const footIsDoer = !!doer;
  const footBot = footPerson ? assigneeAssistant(footPerson, meta) : null;
  const footName = footBot
    ? `${footBot.name?.trim() || footBot.id} (an assistant)`
    : footPerson
      ? displayName(footPerson, profiles)
      : '';
  // When a doer AND a separate Accountable person both exist, the foot shows BOTH
  // as an overlapping pile — the doer in front (tinted), the Accountable person
  // behind — so a card answers "who is doing it AND who answers for it" without
  // being opened (two-part assignee; the one face change in the dossier redesign).
  // Same person, or only one named, stays a single avatar exactly as before. The
  // doer leads, so footPerson/footBot above already describe the front disc.
  const acctPerson = issue.assignee?.trim() ?? '';
  const pilePeople = !!doer && !!acctPerson && doer !== acctPerson;
  const acctBot = pilePeople ? assigneeAssistant(acctPerson, meta) : null;

  /*
   * ── THE "WHAT'S THIS ABOUT" SUMMARY + MANY BLOCKERS — card-face ⑤ ──────────
   *
   * Two things a closed card now carries without being opened: WHAT IT IS ABOUT
   * (the plain-English summary, a customer ask) and, when it waits on more than
   * one thing, WHAT ALL of those are. The two never compete — the blocker reads
   * first in amber/red, the summary is the quieter grey line beneath the title.
   */
  // The blocker LIST (#456). A card waiting on two or more things lists them
  // under the title (Option A: up to two, then "+N more"); one blocker keeps the
  // single terse flag. blockersOf derives one entry for cards written before the
  // list existed, so nothing reads differently the day this ships.
  const entries = blockersOf(issue);
  const multiBlocked = entries.length >= 2;
  // The card-level flag for a many-blocker card: "Blocked Nd", escalating to red
  // on the same two-week line as the single badge — it does NOT multiply per
  // entry, which carry their own detail in the list below.
  const multiDays = ageInDays(issue.blockedAt, now);
  const multiStuck = multiDays !== null && multiDays > BLOCK_STUCK_DAYS;
  // The summary shows on the face only when it EXISTS — most cards leave it
  // blank, and an always-on line would read as broken and leave the column
  // ragged. A many-blocker card collapses it to a quiet foot glyph so the
  // blockers win the vertical space: the one case the line gives way.
  const summary = issue.summary?.trim() ?? '';
  const showSummary = summary !== '' && !multiBlocked;
  const summaryInFoot = summary !== '' && multiBlocked;
  // On a summary-less card, a hover-only "+ What's this about?" ghost invites
  // one — zero height at rest, so it never adds height or raggedness until the
  // card is hovered. Not on a resolved card: there is no "why" to add now.
  const canAddSummary = summary === '' && !multiBlocked && !resolved && summaryEditable;

  // The Layer-A attention chip (needs-attention.ts, mock 04) — the one `.rag` flag
  // the card wears on its face. It gives way to the blocker/hold line, which must
  // read first (R23): a card already shouting what it waits on does not also wear a
  // second flag, so the chip shows only when there is no blocker/hold line here.
  const attnChip = useAttentionChip(issue.ref);
  const showAttnChip = attnChip !== null && issue.heldAt == null && !multiBlocked && !badge;

  /*
   * THE AREA IS RESOLVED HERE, not passed in, because a retired one still has
   * to draw: the card keeps the area it was given and the settings screen is
   * the only place that stops offering it.
   */
  const area = areas.find((a) => a.id === issue.area);
  /*
   * THE PHASE IS RESOLVED HERE TOO, and for the same reason as the area — #377,
   * WS-4. A card knows which phase it runs in (the Plan reads it), but the card
   * on the board never said so, so the board and the Plan told different
   * stories. It is the same chip the open card draws — same round status dot,
   * planned/active/done — so nothing new is learned. A retired phase still
   * draws, like a retired area: the card keeps what it was given.
   */
  const phase = phases.find((p) => p.id === issue.phase);
  // A target that lands AFTER this card's phase is due to end — the same at-risk
  // test the open card makes (IssueModal `duePastPhase`), lifted onto the card
  // face so a slipped card flags itself without being opened. It was the
  // highest-value date signal, hidden in the modal's edit view (both witnesses).
  // Plain YYYY-MM-DD days compare by day; only while the card carries a due mark.
  const duePastPhase = dueMark && !!(phase?.targetEnd && issue.dueDate && issue.dueDate > phase.targetEnd);

  return (
    // Opens on click, on Enter and on Space, and takes focus in reading order.
    // It was a plain div, so a board of seventeen cards had none that could be
    // reached without a mouse and none that a screen reader announced as
    // openable — the whole board was mouse-only. The label carries the reference
    // as well as the title, because "#206" is what people say to each other.
    <div
      /*
       * Joined rather than interpolated, so a card with nothing new is exactly
       * `class="card"` and not `class="card "`. The trailing space broke the
       * accessibility check that counts cards by that attribute — which is a
       * daft thing to be brittle about, and also the check doing its job: it
       * noticed the markup changed under it.
       */
      className={['card', area && 'card-railed', epicT && 'card-epic', isNew && `card-new is-${isNew}`, selMode && 'sel-mode', selected && 'is-selected'].filter(Boolean).join(' ')}
      aria-selected={selected || undefined}
      /* The board's doer-capture prompt (A2) anchors to the card just moved into
         In Progress — it finds that card in the DOM by this id. */
      data-issue-id={issue.id}
      /* Two planes, two questions: --area paints the thin left rail (WHERE the work
         sits), --epic-rgb the whole-card wash (WHAT theme it belongs to). A card can
         carry both. The wash recipe lives in styles.css (.card-epic). */
      style={{
        ...(area && { ['--area']: area.colour }),
        ...(epicT && { ['--epic-rgb']: `var(${epicT.rgb})` }),
      } as React.CSSProperties}
      role={selMode ? 'checkbox' : 'button'}
      aria-checked={selMode ? selected : undefined}
      tabIndex={0}
      /*
       * The mark is a colour, so it is also said in words. Somebody using a
       * screen reader gets the same news, and so does anybody who cannot tell
       * the green from the blue — which is one man in twelve.
       */
      aria-label={`${issue.ref}: ${issue.title}${area ? `. Area: ${area.name}` : ''}${issue.epic ? `. Epic: ${issue.epic}` : ''}${phase ? `. Phase: ${phase.name}` : ''}${NEW_SAYS[isNew ?? ''] ?? ''}`}
      title={NEW_TITLE[isNew ?? ''] ?? undefined}
      draggable={canMove && !sample.readOnly}
      onDragStart={(e) => e.dataTransfer.setData('text/plain', issue.id)}
      onClick={(e) => {
        // IN SELECT MODE the whole tile is the hit target: a click ticks the card
        // (Shift-click extends a range) and NEVER opens it, so there is no accidental
        // open. OUT of select mode a click simply OPENS, exactly as it always has.
        if (selMode) {
          e.preventDefault();
          if (e.shiftKey) rangeSel(issue.id);
          else toggleSel(issue.id);
          return;
        }
        openIssue(issue.id);
      }}
      onKeyDown={(e) => {
        if (selMode) {
          // In select mode the card is a checkbox: Enter, Space and X all tick it.
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'x' || e.key === 'X') {
            e.preventDefault();
            toggleSel(issue.id);
          }
          return;
        }
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault(); // Space would otherwise scroll the column
        openIssue(issue.id);
      }}
    >
      {/* THE SELECTION CHECKBOX — a 20px tick at the TOP-LEFT, inside the content
          gutter so it clears the area/unread rails (pinned at the card edge) and never
          overlaps the Blocked flag on the top row (R30 rework). A state MARKER, not the
          hit area: the whole tile is clickable, so this is aria-hidden and takes no
          clicks. Shown only in select mode (CSS, .sel-mode); the content glides right
          to make the gutter so nothing is clipped (R1/R23), and the column height never
          changes. */}
      {selMode && (
        <span className={`card-select${selected ? ' on' : ''}`} aria-hidden="true">
          <svg className="ic" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
      )}
      {/* The top row: which theme this belongs to on the left, what it is
          waiting on to the right. The epic was not on the card at all before, so
          a column of titles gave no sense of what it was made of; and the
          blocker sat above the title competing with it for the first read. */}
      {/*
        * THE RAIL SAYS WHAT IT IS ON HOVER, which Vinnie asked for by name on
        * 26 August 2026: "you must be able to hover your mouse on the colored
        * sidebar and get a hint that says what that color is". A colour with no
        * way to ask what it means is a legend somebody has to keep in their
        * head — and for one man in twelve it is not there at all. The name is
        * in the card's spoken label as well, so this is the third way of
        * saying it rather than the only one.
        */}
      {area && (
        <span
          className="card-area"
          title={`Area: ${area.name}${area.retiredAt ? ' (retired)' : ''}`}
        />
      )}
      {/* The face carries what the card is WAITING ON above the title, and (just
          below) what it is ABOUT — the two things worth seeing without opening it.
          A card waiting on two-or-more things shows a card-level flag here and
          lists them under the title (Option A); one blocker keeps the single
          terse badge. The phase, epic, size and checks moved to the opened card
          in the density re-cut, so the title stays the first thing read. */}
      {issue.heldAt != null ? (
        <div className="card-top">
          {/* ON HOLD wins the card's top line — a deliberately paused card is parked
              (project-card-hold). Reads red as "On Hold", consistent with the badge on
              the open card (Vinnie, 25 Oct); the reason rides on hover when one was given. */}
          <span className="flag-loz card-hold" title={issue.holdReason || 'On Hold'}>
            On Hold
          </span>
        </div>
      ) : multiBlocked ? (
        <div className="card-top">
          {/* CARD-LEVEL — "Blocked Nd", escalating to red at a fortnight like the
              single badge. It does not name one target: the list below does. */}
          <span
            className={`flag-loz card-flag${multiStuck ? ' is-stuck' : ''}`}
            title={`Waiting on ${entries.length} things — open the card to see them all`}
          >
            <Icon name="flag" size={11} />
            {'Blocked' + (multiDays !== null ? ` ${multiDays}d` : '')}
          </span>
        </div>
      ) : badge ? (
        <div className="card-top">
          {/* ONE LINE, and the whole reason on hover — see blockedBadge. The
              card no longer states its block three ways (flag, summary, and a
              four-line paragraph); it is a list row again, and nothing is
              clipped because the badge is built to fit. */}
          <span className={`flag-loz card-flag${badge.stuck ? ' is-stuck' : ''}`} title={badge.full}>
            <Icon name="flag" size={11} />
            {badge.text}
          </span>
        </div>
      ) : null}
      <div className="card-title">{issue.title}</div>
      {/* ≥2 blockers — up to two, each a LINK to a card (with its live status dot)
          or a PERSON, its reason truncating to one line; then "+N more — open to
          see". The summary gives way to a foot glyph in this state (below). */}
      {multiBlocked && (
        <ul className="card-blk-list">
          {entries.slice(0, 2).map((e, i) => {
            const cardRef = e.refs.find((r) => r.kind === 'card');
            const personRef = e.refs.find((r) => r.kind === 'person');
            // A linked card's status is read FRESH, never frozen — a done target
            // reads as cleared (green dot) without the card being re-saved.
            const linked = cardRef ? allIssues.find((c) => c.ref === cardRef.ref) : undefined;
            const cleared = !!linked && (linked.status === 'done' || linked.status === 'wontdo');
            const why = (e.text && e.text.trim()) || BLOCKER_HEAD[e.type];
            return (
              <li className="card-blk" key={i}>
                <Icon
                  name={cardRef ? 'link' : personRef ? 'clock' : 'blocked'}
                  size={11}
                  className="card-blk-ic"
                />
                {cardRef ? (
                  <span className="card-blk-ref">
                    <span className={`card-blk-dot${cleared ? ' is-done' : ' is-open'}`} />
                    {cardRef.ref}
                  </span>
                ) : personRef ? (
                  <span className="card-blk-ref">@{personRef.label || personRef.ref.split('@')[0]}</span>
                ) : null}
                <span className="card-blk-why" title={why}>{why}</span>
              </li>
            );
          })}
          {entries.length > 2 && (
            <li className="card-blk-more">+ {entries.length - 2} more — open to see</li>
          )}
        </ul>
      )}
      {/* The plain-English "what is this about", present-only — the quieter grey
          line under the title. Blank on most cards, where it takes no height and
          leaves the column even. Two lines, then it clamps. */}
      {showSummary && <div className="card-summary">{summary}</div>}
      {/* The authoring nudge on a summary-less card — zero height at rest, revealed
          on hover (or keyboard focus), so it never adds height until asked for. It
          opens the card, where the "What is this about?" field takes the sentence. */}
      {canAddSummary && (
        <button
          type="button"
          {...inert('card-add-summary', (e) => { e.stopPropagation(); openIssue(issue.id, 'summary'); }, 'Add a one-line summary so the card says what it is about')}
        >
          + What’s this about?
        </button>
      )}
      {/* The attention chip — one `.rag` flag (R3) carrying the short cause, under
          the summary so the blocker line still reads first (R23). The full sentence
          and the fix live in the Needs Attention list. */}
      {showAttnChip && (
        <div className="card-attn">
          <span className={`rag ${attnChip!.severity === 'red' ? 'r' : 'a'}`} title={attnChip!.chip}>
            <span className="dot" />
            {attnChip!.chip}
          </span>
        </div>
      )}
      {!resolved && progHealth.kind !== 'none' && (
        <div className="card-prog">
          <ProgressMini
            health={progHealth}
            source={eff.source}
            checklistCount={eff.total != null ? { done: eff.done!, total: eff.total } : null}
          />
        </div>
      )}
      {/* THE LABELS — present-only (no reserved height when a card has none, so the
          column never goes ragged, R23). Its OWN row above the one-line foot, so the
          foot stays nowrap. Compact chips (Vinnie's pick over dots), capped at four
          with a +N, each wearing its label colour; full set on hover/aria. */}
      {(issue.labels?.length ?? 0) > 0 && (
        <div className="card-labels" title={issue.labels!.join(', ')}>
          {issue.labels!.slice(0, 4).map((name) => {
            const colour = labelColour(name, labelReg);
            return (
              <span key={name} className={`card-label-chip${colour ? ' has-colour' : ''}`}>
                {colour && <span className="label-dot" style={{ background: colour }} aria-hidden="true" />}
                {name}
              </span>
            );
          })}
          {issue.labels!.length > 4 && (
            <span className="card-label-more">+{issue.labels!.length - 4}</span>
          )}
        </div>
      )}
      <div className="card-foot">
        <span className="ref">
          {/*
            * ── THE MARK FOLLOWS THE MEANING, NOT THE SPELLING — #302 ────────
            *
            * This read `issue.kind === 'bug'`. A board renaming Bug to
            * "Defect" lost the mark from every card, with nothing to say so.
            * Now every Fixing type carries it, so a board with both "Defect"
            * and "Incident" gets both marked — Vinnie, 31 August 2026, choosing
            * that over one type nominated per board: *"picking one by hand is a
            * setting that will be got wrong once and never revisited."*
            */}
          {/* THE TYPE, FOR EVERY KIND — not only bugs. A Fixing type keeps the
              bug mark in its own amber; Building and Upkeep get a star and a
              checkbox in the ref's muted ink, so a column's mix of work reads at
              a glance. The mark comes from meansMark, the one place this and the
              opened card both read, so a type can never be drawn two ways (it was
              — a wrench here, a gear there; Vinnie, 14 Sep). Driven by MEANS, so
              a renamed type keeps its mark (#302). */}
          {marked(issue.kind, issueTypes) ? (
            <Icon name="bug" size={11} className="ref-bug" />
          ) : (
            <Icon
              name={meansMark(meansOf(issue.kind, issueTypes))}
              size={11}
              className="type-mark"
            />
          )}
          {issue.ref}
        </span>
        <Priority priority={issue.priority} markOnly />
        {/* THE ONE CONTEXTUAL MARK (see the note where these are worked out).
            Only one of the three ever renders, so the foot stays a short row. */}
        {/* The card's target date (Track B) — takes the slot ahead of age and
            comments. Muted when comfortably ahead, amber when due soon, red when
            overdue (cardDueState); no new colour, the same amber/red the rest of
            the card uses. */}
        {dueMark && issue.dueDate && (() => {
          // The slip mark rides the existing due chip as a trailing "· 3×" — no
          // new rail, no second glyph, one contextual mark per face. It fires only
          // after two net-later pushes (migration 0125, targetPushes is live-gated:
          // a date moved before work starts is re-planning, not slippage). The hover
          // spells out how far the target has drifted from where it was first set.
          const pushes = targetPushes(issue);
          const marked = pushes >= PUSH_MARK_THRESHOLD;
          const slip = marked ? dueSlip(issue) : null;
          // ONE MERGED LATE CHIP — when the target lands after the card's phase is
          // due to end, the DUE CHIP itself is the at-risk mark: it leads with the
          // caution glyph and turns red, rather than a SECOND red triangle sitting
          // beside it. The foot is one line and never wraps (Vinnie, 10 Oct), so a
          // second glyph is a second thing that can push the row over — the signal
          // lives in the one chip, with the reason on hover and for a screen reader.
          const atRisk = duePastPhase && !!phase;
          const late = dueState === 'overdue' || atRisk;
          const dueTitle =
            slip && slip.slipDays != null && slip.slipDays > 0 && slip.baseline
              ? `Target pushed ${pushes}× — now ${slip.slipDays} day${slip.slipDays === 1 ? '' : 's'} later than first set (${shortDay(slip.baseline)} → ${shortDay(issue.dueDate!)}).`
              : `Due ${dueFull(issue.dueDate!)}`;
          const title = atRisk
            ? `After the ${phase!.name} phase ends (${phase!.targetEnd ? shortDay(phase!.targetEnd) : ''}) — this card is at risk. ${dueTitle}`
            : dueTitle;
          return (
            <span
              className={`card-due${late ? ' is-over' : dueState === 'due-soon' ? ' is-soon' : ''}${atRisk ? ' is-atrisk' : ''}`}
              title={title}
              {...(atRisk
                ? {
                    role: 'img' as const,
                    'aria-label': `At risk — target lands after the ${phase!.name} phase ends${phase!.targetEnd ? ` (${shortDay(phase!.targetEnd)})` : ''}.`,
                  }
                : {})}
            >
              <Icon name={late ? 'warning' : 'calendar'} size={11} />
              {dueChip(issue.dueDate!, dueState, now)}
              {marked && <span className="card-push"> · {pushes}×</span>}
            </span>
          );
        })()}
        {/* How long a working card has sat where it is — shown only once it beats
            the board's own norm, in the priority mark's amber (card-age.ts). A
            card within the norm shows nothing, so there is no meaningless "0d". */}
        {ageMark && aging && (
          <span
            className="card-age is-aged"
            title={`${STATUS_LABELS[issue.status]} ${aging.days} day${aging.days === 1 ? '' : 's'} — longer than most cards on this board`}
          >
            <Icon name="clock" size={11} />
            {aging.days}d
          </span>
        )}
        {/* When a card finished — the completed_at the database records (0096).
            A Done card was previously indistinguishable from a live one. */}
        {finishMark && issue.completedAt && (
          <span className="card-when" title={`Finished ${finishFull(issue.completedAt)}`}>
            <Icon name="check" size={11} />
            {finishShort(issue.completedAt)}
          </span>
        )}
        {/* A many-blocker card gave its under-title space to the blockers, but it
            still says it has a "what is this about" to read — a quiet notes glyph
            here, the whole summary on hover. The one place the summary collapses. */}
        {summaryInFoot && (
          <span className="card-notes" title={summary}>
            <Icon name="list-bullet" size={11} />
          </span>
        )}
        {/* A card with files attached showed nothing on the board — the dossier's
            Files section (4.0.11) was invisible from the face, so the only way to
            learn a document was there was to open the card. A quiet document glyph
            with the count says it plainly. Shown on its own terms, not through the
            one contextual mark, because "this has files" is a different fact from
            when the card is due; kept minimal so the one-line foot never wraps
            (R23). A paperclip — the near-universal "has an attachment" mark — reads
            as "this card carries a file" more plainly than a plain page did. */}
        {(issue.attachments?.length ?? 0) > 0 && (
          <span
            className="card-files"
            title={`${issue.attachments!.length} file${issue.attachments!.length === 1 ? '' : 's'} attached`}
          >
            <Icon name="paperclip" size={14} />
            {issue.attachments!.length}
          </span>
        )}
        {/* A card carrying a conversation looked exactly like one with none, so
            the only way to find a comment was to open every card in turn — or
            to notice it going past in Activity. Shown when it is neither aged
            nor freshly finished — the more urgent marks take the slot first. */}
        {commentMark && (
          <span
            className="card-comments"
            title={`${shown.length} comment${shown.length === 1 ? '' : 's'}`}
          >
            <Icon name="comment" size={11} />
            {shown.length}
          </span>
        )}
        {footPerson && (
          /* The doer (or the Accountable fallback) with a role glyph. A machine
             holding a card is worth seeing from across the board — what you do
             about a stalled item depends on whether a person or an assistant has
             it — so an assistant keeps the sparkle avatar. One line, never wraps. */
          <span
            className="card-who"
            title={footIsDoer ? `${footName} is doing this` : `${footName} is accountable`}
          >
            <Icon name={footIsDoer ? 'person' : 'shield'} size={13} className="card-who-role" />
            {/* The vendor name-tag, compact on the tight foot — the glyph alone, sitting
                just before the avatar, so the one-line tile never wraps (R23, B-07).
                Renders nothing when the foot person is not an external vendor. */}
            <VendorTag who={footPerson} profiles={profiles} glyphOnly />
            {pilePeople ? (
              /* The doer in front (tinted) + the Accountable person behind — the
                 open-card pile at the 22px foot size. A bot in either seat keeps
                 its sparkle. Accountable disc second in the DOM so the tinted doer
                 (z-index) paints on top, matching the open card's stack. */
              <span className="cardface-avs card-who-pile">
                <span className="avatar is-doer" title={`${footName} is doing this`}>
                  {footBot ? <Icon name="sparkle" size={11} /> : initials(doer, profiles)}
                </span>
                <span className="avatar" title={`${displayName(acctPerson, profiles)} is accountable`}>
                  {acctBot ? <Icon name="sparkle" size={11} /> : initials(acctPerson, profiles)}
                </span>
              </span>
            ) : footBot ? (
              <span className="avatar is-assistant">
                <Icon name="sparkle" size={11} />
              </span>
            ) : (
              <span className="avatar">{initials(footPerson, profiles)}</span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
